import { useIsDoctor, useIsOperator, useOrgAdminOf } from '../auth.ts';
import { AuthPageShell } from './AuthPageShell.tsx';

type Role = 'doctor' | 'operator' | 'org_admin';

interface RolePageShellProps {
  /** Required role for this page. Non-matching users see a 403 banner. */
  role: Role;
  children: React.ReactNode;
  maxWidth?: string;
  padTop?: string;
  flexMain?: boolean;
}

const FORBIDDEN_MESSAGE: Record<Role, string> = {
  doctor:   'U heeft geen toegang tot dit scherm. Enkel artsen hebben toegang tot administratie.',
  operator: 'U heeft geen toegang tot dit scherm. Enkel beheerders hebben toegang.',
  org_admin: 'U heeft geen toegang tot dit scherm. Enkel praktijkbeheerders hebben toegang.',
};

/**
 * Generic role-guarded layout wrapper.
 * Renders children only when the authenticated user holds the required role;
 * otherwise shows a forbidden message.
 *
 * Used as the shared foundation for `OperatorPageShell`.
 */
export function RolePageShell({ role, children, maxWidth, padTop, flexMain }: RolePageShellProps) {
  const isDoctor   = useIsDoctor();
  const isOperator = useIsOperator();
  const orgAdminOf = useOrgAdminOf();
  const hasAccess  = role === 'doctor' ? isDoctor : role === 'operator' ? isOperator : orgAdminOf.length > 0;

  if (!hasAccess) {
    return (
      <AuthPageShell>
        <p className="text-muted status-message">{FORBIDDEN_MESSAGE[role]}</p>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell maxWidth={maxWidth} padTop={padTop} flexMain={flexMain}>
      {children}
    </AuthPageShell>
  );
}
